import { useState } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import {
  Bot,
  Check,
  ChevronsUpDown,
  GalleryVerticalEnd,
  List,
  LogOut,
  Logs,
  PhoneOutgoing,
  Plus,
  Settings,
  Settings2,
  Users,
} from 'lucide-react';
import { ChartBar } from 'lucide-react';
import { useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { IAccount, IUser } from '@/types';
import axiosClient from '@/utils/axiosClient';
import { ScrollArea } from './ui/scroll-area';

const navigation = [
  // {
  //   name: 'Dashboard',
  //   href: '/',
  //   icon: ChartColumnDecreasingIcon,
  // },

  {
    name: 'Calls',
    href: '/calls',
    icon: PhoneOutgoing,
  },
  {
    name: 'Contacts',
    href: '/contacts',
    icon: Users,
  },
  {
    name: 'Campaigns',
    href: '/campaigns',
    icon: List,
  },
];
const scenarioNavigation = [
  {
    name: 'Scenarios',
    href: '/scenarios',
    icon: Bot,
  },
  {
    name: 'Call History',
    href: '/call_scenarios',
    icon: Logs,
  },
];

interface AppSidebarProps {
  currentUser: IUser;
}

const AppSidebar = ({ currentUser }: AppSidebarProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const modal = useModal('add-account-modal');

  const [selectedAccount, setSelectedAccount] = useState(
    currentUser.accounts.find((account) => account.id === currentUser.selected_account_id) as IAccount,
  );
  const accounts = currentUser.accounts;

  const handleAccountChange = async (account: IAccount) => {
    if (selectedAccount?.id === account.id) return;

    try {
      const response = await axiosClient.put(`v1/users/${currentUser.id}`, {
        selected_account_id: account.id,
      });
      if (response.status === 200) {
        setSelectedAccount(account);
        queryClient.clear();
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error saving team member:', error);
    }
  };

  const handleLogout = () => {
    navigate('/logout');
  };

  const sortedAccounts = accounts.sort((a: IAccount, b: IAccount) => {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });

  return (
    <>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <div className="flex items-center justify-center rounded-lg aspect-square size-8 bg-sidebar-primary text-sidebar-primary-foreground">
                    <GalleryVerticalEnd className="size-4" />
                  </div>
                  <div className="grid flex-1 text-sm leading-tight text-left">
                    <span className="font-semibold truncate">{selectedAccount.name}</span>
                    <span className="text-xs truncate">Basic Plan</span>
                  </div>
                  <ChevronsUpDown className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg "
                align="start"
                side="bottom"
                sideOffset={4}
              >
                <DropdownMenuLabel className="text-xs text-muted-foreground">Account</DropdownMenuLabel>
                <ScrollArea className="flex flex-col overflow-y-auto max-h-40">
                  {sortedAccounts.map((account) => (
                    <DropdownMenuItem
                      key={account.name}
                      onClick={() => handleAccountChange(account)}
                      className="flex justify-between gap-2 p-2 cursor-pointer"
                    >
                      <div className="flex gap-2">
                        <div className="flex items-center justify-center border rounded-sm size-6">
                          <GalleryVerticalEnd className="size-4 shrink-0" />
                        </div>
                        <div className="mr-3 line-clamp-1">{account.name}</div>
                      </div>

                      {selectedAccount.id === account.id && <Check className="justify-end text-right size-4" />}
                    </DropdownMenuItem>
                  ))}
                </ScrollArea>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="gap-2 p-2" onClick={() => modal.show()}>
                  <div className="flex items-center justify-center border rounded-md size-6 bg-background">
                    <Plus className="size-4" />
                  </div>
                  <div className="font-medium text-muted-foreground">Add account</div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {selectedAccount.feature_flags.includes('campaign_manager') && (
          <>
            <SidebarSeparator className="mx-0" />
            <SidebarGroup>
              <SidebarGroupLabel>Campaign Manager</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navigation.map((item) => (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton asChild>
                        <Link to={item.href}>
                          <item.icon />
                          <span>{item.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
        {selectedAccount.feature_flags.includes('kpi_dashboard') && (
          <>
            <SidebarSeparator className="mx-0" />
            <SidebarGroup>
              <SidebarGroupLabel>Analytics</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild>
                      <Link to="/dashboard">
                        <ChartBar />
                        <span>KPI Dashboard</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
        {selectedAccount.feature_flags.includes('call_scenario_trainer') && (
          <>
            <SidebarSeparator className="mx-0" />
            <SidebarGroup>
              <SidebarGroupLabel>Call Scenario Trainer</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {scenarioNavigation.map((item) => (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton asChild>
                        <Link to={item.href}>
                          <item.icon />
                          <span>{item.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator className="mx-0" />
          </>
        )}
      </SidebarContent>

      <SidebarRail />
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="w-8 h-8 rounded-lg">
                    <AvatarImage src={currentUser.avatar} alt={currentUser.name} />
                    <AvatarFallback className="rounded-lg">{currentUser.initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-sm leading-tight text-left">
                    <span className="font-semibold truncate">{currentUser.name}</span>
                    <span className="text-xs truncate">{currentUser.email}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="w-8 h-8 rounded-lg">
                      <AvatarImage src={currentUser.avatar} alt={currentUser.name} />
                      <AvatarFallback className="rounded-lg">{currentUser.initials}</AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-sm leading-tight text-left">
                      <span className="font-semibold truncate">{currentUser.name}</span>
                      <span className="text-xs truncate">{currentUser.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link to="/billing">
                      <Sparkles />
                      Upgrade to Pro
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator /> */}
                <DropdownMenuGroup>
                  {/* <DropdownMenuItem asChild>
                    <Link to="/settings">
                      <Key />
                      API Credentials
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/billing">
                      <CreditCard />
                      Billing
                    </Link>
                  </DropdownMenuItem> */}
                  {/* <DropdownMenuItem asChild>
                    <Link to="/integrations">
                      <Settings2 />
                      Integrations
                    </Link>
                  </DropdownMenuItem> */}
                  {/* <DropdownMenuItem asChild>
                    <Link to="/webhooks">
                      <Logs />
                      Webhook Logs
                    </Link>
                  </DropdownMenuItem> */}
                  <DropdownMenuItem asChild>
                    <Link to="/team">
                      <Users />
                      Team Members
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/account-settings">
                      <Settings2 />
                      Account Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings">
                      <Settings />
                      My Profile
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <AlertDialog>
                    <AlertDialogTrigger className="w-full">
                      <div className="flex gap-2 pt-1 pl-2.5 text-sm w-full">
                        <LogOut size={'16'} />
                        Log out
                      </div>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
                        <AlertDialogDescription>
                          We will log you out of the application at this time.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleLogout}>Logout</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </>
  );
};

export default AppSidebar;
